import { gql } from "@apollo/client";
import { batch } from "react-redux";
import { captureException } from "@sentry/browser";

import type { AppState } from "../reducers";
import Tracker from "../services/Tracker";

import {
  ACCOUNT_PROFILE_SET,
  ACCOUNT_UPDATEPROFILE_ERROR,
  ACCOUNT_UPDATEPROFILE_SUCCESS,
  FORM_RESET_ALL,
  ACCOUNT_LOAD_CARDS,
  ACCOUNT_SET_CARDS,
  ACCOUNT_ORDERS_ERROR,
  ACCOUNT_ORDERS_SET,
  ACCOUNT_ORDERS_LOAD_MORE_LOADING,
  ACCOUNT_AUTOMATICDEBIT_LOADING,
  ACCOUNT_AUTOMATICDEBIT_ERROR,
  ACCOUNT_AUTOMATICDEBIT_SUCCESS,
  ACCOUNT_EDENREDUSERNAME_SET,
  ACCOUNT_CREDITS_LOADING,
  ACCOUNT_CREDITS_SUCCESS,
  ACCOUNT_CREDITS_ERROR,
  ACCOUNT_ORDERFEEDBACK_AVAILABLE,
  ACCOUNT_ORDERSCOUNT_SET,
  ACCOUNT_SPONSORSHIPCODE_LOADING,
  ACCOUNT_SPONSORSHIPCODE_SUCCESS,
  ACCOUNT_SPONSORSHIPCODE_ERROR,
  ACCOUNT_SPONSORSHIPEMAIL_SUCCESS,
  ACCOUNT_SPONSORSHIPEMAIL_ERROR,
  ACCOUNT_SPONSORSHIPEMAIL_LOADING,
  APP_LOADING_END,
  ACCOUNT_TERMS_SUCCESS,
  ACCOUNT_TERMS_ERROR,
  ACCOUNT_TERMS_AVAILABLE,
  APP_LOADING_START,
  ACCOUNT_SET_NOTIFICATIONS,
  TOAST_ADD,
  LOYALTY_PROGRAM_WALLET_SET,
  LOYALTY_PROGRAM_MOVEMENTS_SET,
} from "./types";

import { checkTokenFromCookieThenAuthenticate } from "./auth";
import { getClickAndCollectConfiguration } from "./collect";
import { formHandleInput } from "./form";
import { apollo, AppDispatch, AppThunk } from "./index";
import * as rootNavigation from "../navigators/RootNavigatorRef";
import { AnyAction } from "redux";
import { addToast } from "./toast";
import moment from "moment";
import { isOrderFeedbackPossible } from "../helpers/order";
import CookieManager from "@/services/CookieManager";
import config from "../config/config";
import * as RootNavigation from "../navigators/RootNavigatorRef";
import dayjs from "dayjs";
import type { TranslateOptions } from "@/i18n";
import { translateMessage } from "@/helpers/translation";

const translateAccountMessage = (key: string, options?: TranslateOptions): string =>
  translateMessage(key, options);

export const acceptTerms = (): AppThunk => async (dispatch: AppDispatch) => {
  dispatch({ type: APP_LOADING_START });

  try {
    await apollo().mutate<any>({
      variables: {},
      mutation: gql`
        mutation {
          acceptTerms
        }
      `,
    });
    dispatch({ type: APP_LOADING_END });
    dispatch({ type: ACCOUNT_TERMS_SUCCESS });
  } catch (err) {
    captureException(err);
    dispatch({ type: APP_LOADING_END });
    dispatch({ type: ACCOUNT_TERMS_ERROR });
  }
};

export const updatePassword =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    // User changes password (My account)
    dispatch({ type: APP_LOADING_START });

    try {
      const {
        form: {
          newPassword: { actualPassword, newPassword },
        },
      } = getState();
      const passwordUpdateSuccessMessage = translateAccountMessage(
        "account.toasts.passwordUpdateSuccess",
        { defaultValue: "Votre mot de passe a bien été modifié" },
      );
      const passwordUpdateErrorMessage = translateAccountMessage(
        "account.toasts.passwordUpdateError",
        { defaultValue: "Une erreur est survenue. Vérifiez votre saisie puis réessayez" },
      );
      const {
        data: { updatePassword },
      } = await apollo().mutate<any>({
        variables: { actualPassword, newPassword },
        mutation: gql`
          mutation ($actualPassword: String!, $newPassword: String!) {
            updatePassword(actualPassword: $actualPassword, newPassword: $newPassword)
          }
        `,
      });

      if (updatePassword) {
        dispatch({ type: APP_LOADING_END });
        dispatch(
          addToast({
            type: "success",
            message: passwordUpdateSuccessMessage,
          }),
        );
        rootNavigation.navigate("profileDetail");
      } else {
        dispatch({ type: APP_LOADING_END });
        dispatch(
          addToast({
            type: "error",
            message: passwordUpdateErrorMessage,
          }),
        );
      }

      dispatch({ type: FORM_RESET_ALL });
    } catch (err) {
      captureException(err);
      dispatch({ type: APP_LOADING_END });
      dispatch(
        addToast({
          type: "error",
          message: translateAccountMessage("account.toasts.passwordUpdateError", {
            defaultValue: "Une erreur est survenue. Vérifiez votre saisie puis réessayez",
          }),
        }),
      );
    }
  };

export const getCustomerProfile =
  (withGlobalConfigurationFetch = true): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    try {
      const req: any = await apollo().query({
        query: gql`
          {
            getCustomerProfile {
              hasAcceptedTerms
              stripeCustomerId
              hasAutomaticDebit
              defaultStripeCardId
              automaticDebitAmount
              defaultPaymentMethod
              payzenPaymentMethodToken
              payzenCardPan
              payzenCardBrand
              payzenCardExpMonth
              payzenCardExpYear
              sponsorshipCode
              withMealVoucher
              isEnrolledToLoyaltyProgram
              sponsor {
                id
              }
              hasSubscribedToCanteenFeedbackEmail
              companyEntity {
                id
                allowMealVoucher
                walletMaximumAmountToRefillByMealVoucher
                walletMaximumAmountToRefillByBankCard
                walletMaximumBalance
                collectiveCateringRegistrationCode
                checkoutSystem
                hasQRCodeEnabled
                hasPhysicalBadgeEnabled
                hasClickAndCollectEnabled
                isBilingualFridgeEnabled
                onboardingDate
                name
                canteenLocations {
                  canteenFridges {
                    openItVendingMachineId
                  }
                }
              }
            }
          }
        `,
      });

      const profile = req.data.getCustomerProfile;

      const hasOpenItVendingMachine = profile?.companyEntity?.canteenLocations?.some(
        (location: any) =>
          location.canteenFridges.some((fridge: any) => fridge.openItVendingMachineId),
      );

      delete profile?.companyEntity?.canteenLocations;

      dispatch({
        type: ACCOUNT_PROFILE_SET,
        profile: {
          ...profile,
          companyEntity: {
            ...profile.companyEntity,
            hasOpenItVendingMachine,
          },
        },
      });

      // Check if user already has a stripeCustomerId
      if (!profile?.stripeCustomerId) {
        dispatch(createStripeCustomer());
      }

      if (withGlobalConfigurationFetch) {
        dispatch(getClickAndCollectConfiguration(false));
      }

      if (profile.hasAcceptedTerms === false) {
        // prompt customer to accept new terms if he don't accepted them yet
        dispatch({ type: ACCOUNT_TERMS_AVAILABLE });
      }
    } catch (err) {
      captureException(err);
    }
    dispatch({ type: APP_LOADING_END });
  };

export const updateCustomer =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    try {
      const { form } = getState();
      const input = form.accountInformations;
      const profileUpdateSuccessMessage = translateAccountMessage(
        "account.toasts.profileUpdateSuccess",
        { defaultValue: "Profil mis à jour" },
      );
      const profileUpdateErrorMessage = translateAccountMessage(
        "account.toasts.profileUpdateError",
        { defaultValue: "Erreur lors de la mise à jour du profil" },
      );

      const {
        data: { updateCustomer: updatedCustomer },
      } = await apollo().mutate<any>({
        variables: { ...input },
        mutation: gql`
          mutation (
            $firstname: String!
            $lastname: String!
            $phone: String
            $hasAcceptedNewsletter: Boolean
            $hasSubscribedToCanteenFeedbackEmail: Boolean
          ) {
            updateCustomer(
              firstname: $firstname
              lastname: $lastname
              phone: $phone
              hasAcceptedNewsletter: $hasAcceptedNewsletter
              hasSubscribedToCanteenFeedbackEmail: $hasSubscribedToCanteenFeedbackEmail
            )
          }
        `,
      });

      if (updatedCustomer) {
        dispatch({ type: ACCOUNT_UPDATEPROFILE_SUCCESS });
        await dispatch(checkTokenFromCookieThenAuthenticate());
        dispatch(addToast({ type: "success", message: profileUpdateSuccessMessage }));
      } else {
        dispatch(
          addToast({
            type: "error",
            message: profileUpdateErrorMessage,
          }),
        );
        dispatch({ type: ACCOUNT_UPDATEPROFILE_ERROR });
      }
    } catch (err) {
      captureException(err);
      dispatch({ type: ACCOUNT_UPDATEPROFILE_ERROR });
    }
    dispatch({ type: APP_LOADING_END });
  };

export const getPaymentCards =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: ACCOUNT_LOAD_CARDS });
    dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    const { profile } = getState().account;

    let stripeCustomerId;

    if (!profile.stripeCustomerId) {
      stripeCustomerId = await dispatch(createStripeCustomer());
    } else {
      stripeCustomerId = profile.stripeCustomerId;
    }

    try {
      const req: any = await apollo().query({
        variables: { stripeCustomerId },
        query: gql`
          query ($stripeCustomerId: String!) {
            listCardsForCustomer(stripeCustomerId: $stripeCustomerId) {
              id
              brand
              country
              customer
              cvc_check
              last4
              exp_month
              exp_year
            }
          }
        `,
      });

      const listCardsForCustomer = req.data.listCardsForCustomer;

      dispatch({ type: ACCOUNT_SET_CARDS, cards: listCardsForCustomer });
    } catch (err) {
      captureException(err);
      dispatch({ type: ACCOUNT_SET_CARDS, cards: [] });
    }
    dispatch({ type: APP_LOADING_END });
  };

export const getEdenredUsername = (): any => async (dispatch: AppDispatch) => {
  try {
    dispatch({ type: APP_LOADING_START });
    await apollo().cache.reset();

    const req: any = await apollo().query({
      query: gql`
        query {
          edenredUsername
        }
      `,
    });

    const edenredUsername = req.data.edenredUsername;

    dispatch({
      type: ACCOUNT_EDENREDUSERNAME_SET,
      edenredUsername,
    });
  } catch (err) {
    captureException(err);
  }
  dispatch({ type: APP_LOADING_END });
};

export const deletePaymentMethod =
  (type: string, cardId?: string): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: ACCOUNT_LOAD_CARDS });
    dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    try {
      const req = await apollo().mutate<any>({
        variables: { type, cardId },
        mutation: gql`
          mutation ($type: String!, $cardId: String) {
            deleteSesamePaymentMethod(type: $type, cardId: $cardId)
          }
        `,
      });

      if (req.data.deleteSesamePaymentMethod) {
        await dispatch(getPaymentCards());
        await dispatch(getEdenredUsername());
        await dispatch(getCustomerProfile(false));
      }
    } catch (err) {
      captureException(err);
    }
    dispatch({ type: APP_LOADING_END });
  };

export const updateDefaultPaymentMethod =
  (paymentMethod: string, paymentMethodId: string = ""): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: APP_LOADING_START });
    try {
      await apollo().cache.reset();
      const {
        data: { updateSesameDefaultPaymentMethod },
      } = await apollo().mutate<any>({
        variables: { paymentMethod, paymentMethodId },
        mutation: gql`
          mutation ($paymentMethod: String!, $paymentMethodId: String) {
            updateSesameDefaultPaymentMethod(
              paymentMethod: $paymentMethod
              paymentMethodId: $paymentMethodId
            )
          }
        `,
      });

      if (updateSesameDefaultPaymentMethod) {
        await dispatch(getPaymentCards());
        await dispatch(getEdenredUsername());
        await dispatch(getCustomerProfile(false));

        Tracker.track("Means Of Payment Modified");
      }
    } catch (err) {
      captureException(err);
    }
    dispatch({ type: APP_LOADING_END });
  };

export const setWithMealVoucher = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: APP_LOADING_START });
  try {
    await apollo().cache.reset();

    const {
      data: { setWithMealVoucher },
    } = await apollo().mutate<any>({
      mutation: gql`
        mutation {
          setWithMealVoucher
        }
      `,
    });

    if (setWithMealVoucher) {
      await dispatch(getCustomerProfile(false));
      rootNavigation.navigate("paymentMethods");

      // Tracker.track("Add Meal Voucher Mean Of Payment");
    }
  } catch (err) {
    captureException(err);
  }
  dispatch({ type: APP_LOADING_END });
};

export const setWithMealVoucherDuringOnboarding = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: APP_LOADING_START });
  try {
    await apollo().cache.reset();

    const {
      data: { setWithMealVoucher },
    } = await apollo().mutate<any>({
      mutation: gql`
        mutation {
          setWithMealVoucher
        }
      `,
    });

    if (setWithMealVoucher) {
      rootNavigation.navigate("home");
    }
  } catch (err) {
    captureException(err);
  }
  dispatch({ type: APP_LOADING_END });
};

export const createStripeCustomer =
  (): AppThunk<Promise<string>> => async (dispatch: AppDispatch) => {
    try {
      const {
        data: { createStripeCustomerId },
      } = await apollo().mutate<any>({
        mutation: gql`
          mutation {
            createStripeCustomerId
          }
        `,
      });

      if (createStripeCustomerId) {
        await dispatch(getCustomerProfile());

        return createStripeCustomerId;
      } else {
        throw new Error("Unable to create Stripe customer profile");
      }
    } catch (err) {
      captureException(err);
    }
  };

export const getCustomerOrders =
  (limit = 8, offset = 0): any =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: ACCOUNT_ORDERS_LOAD_MORE_LOADING });
      dispatch({ type: APP_LOADING_START });

      await apollo().cache.reset();

      const req: any = await apollo().query({
        variables: { limit, offset },
        query: gql`
          query ($limit: Float, $offset: Float) {
            canteenOrders(limit: $limit, offset: $offset) {
              id
              type
              status
              totalWithVat
              deliveryHour
              feedbackDone
              deliveryFees
              deliveryFeesVatRate
              createdAt
              documents {
                createdAt
                fileUrl
              }
              extraCosts {
                id
                name
                price
                quantity
                vatRate
                disputePriceDelta
                disputeQuantityDelta
              }
              products {
                id
                price
                vatRate
                category
                pictureUrl
                quantity
                name
                openItErrorType
                disputePriceDelta
                disputeQuantityDelta
              }
              dispute {
                id
                totalDeltaWithVat
              }
            }
          }
        `,
      });

      const customerOrders = req.data.canteenOrders;

      if (customerOrders && customerOrders.length > 0) {
        const order = customerOrders[0];
        const isLastOrderLessThanThreeDaysAgo = moment(order.deliveryHour).isSameOrAfter(
          moment().subtract(3, "day"),
        );

        const feedbackAlreadyAsked = await CookieManager.get(config.cookies.url)
          .then((cookies: any) => {
            return cookies[`prompt_order_feedback_${order.id}`]?.value as string | undefined;
          })
          .catch((err: any) => false);

        if (
          isLastOrderLessThanThreeDaysAgo &&
          isOrderFeedbackPossible(order) &&
          !feedbackAlreadyAsked
        ) {
          await CookieManager.set(config.cookies.url, {
            name: `prompt_order_feedback_${order.id}`,
            value: "true",
            path: "/",
            version: "1",
            expires: dayjs().add(4, "days").format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
          });
          RootNavigation.navigate("orderFeedback", { order });
        }
      }

      dispatch({ type: ACCOUNT_ORDERS_SET, orders: customerOrders });
    } catch (err) {
      captureException(err);
      dispatch({ type: ACCOUNT_ORDERS_ERROR });
    }
    dispatch({ type: APP_LOADING_END });
  };

export const getAppNotifications =
  (limit = 10, offset = 0): any =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: APP_LOADING_START });

      await apollo().cache.reset();

      const req: any = await apollo().query({
        variables: { limit, offset },
        query: gql`
          query {
            appNotificationCustomers {
              appNotification {
                redirectURI
                title
                content
              }
              isOpened
              id
              createdAt
            }
          }
        `,
      });

      const customerNotifications = req.data.appNotificationCustomers;
      dispatch({
        type: ACCOUNT_SET_NOTIFICATIONS,
        notifications: customerNotifications,
      });
    } catch (err) {
      console.error(err);
      captureException(err);
      dispatch({ type: ACCOUNT_ORDERS_ERROR });
    }
    dispatch({ type: APP_LOADING_END });
  };

export const setAppNotificationRead =
  (selectedNotification: string): any =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: APP_LOADING_START });

      await apollo().cache.reset();

      await apollo().mutate({
        variables: {
          setAppNotificationReadId: selectedNotification,
        },
        mutation: gql`
          mutation SetAppNotificationRead($setAppNotificationReadId: String) {
            setAppNotificationCustomerRead(id: $setAppNotificationReadId)
          }
        `,
      });
      dispatch(getAppNotifications());
    } catch (err) {
      console.error(err);
      captureException(err);
      dispatch({ type: ACCOUNT_ORDERS_ERROR });
    }
    dispatch({ type: APP_LOADING_END });
  };

export const openOrderFeedback =
  (orderId: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch({
      type: ACCOUNT_ORDERFEEDBACK_AVAILABLE,
      available: true,
      id: orderId,
    });
  };

export const setAutomaticDebitPreferences =
  (): AppThunk<Promise<boolean | undefined>> =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: ACCOUNT_AUTOMATICDEBIT_LOADING });
    dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    const { form } = getState();
    const { hasAutomaticDebit, automaticDebitAmount } = form.automaticDebit;

    try {
      const res = await apollo().mutate<any>({
        variables: { hasAutomaticDebit, automaticDebitAmount },
        mutation: gql`
          mutation ($hasAutomaticDebit: Boolean!, $automaticDebitAmount: Float!) {
            setAutomaticDebitPreferences(
              hasAutomaticDebit: $hasAutomaticDebit
              automaticDebitAmount: $automaticDebitAmount
            ) {
              hasAutomaticDebit
              automaticDebitAmount
            }
          }
        `,
      });

      batch(() => {
        dispatch(
          formHandleInput(
            "automaticDebit",
            "automaticDebitAmount",
            res.data.setAutomaticDebitPreferences.automaticDebitAmount,
          ),
        );
        dispatch(
          formHandleInput(
            "automaticDebit",
            "hasAutomaticDebit",
            res.data.setAutomaticDebitPreferences.hasAutomaticDebit,
          ),
        );

        dispatch({ type: ACCOUNT_AUTOMATICDEBIT_SUCCESS });
      });

      return true;
    } catch (err) {
      captureException(err);

      batch(() => {
        dispatch({ type: ACCOUNT_AUTOMATICDEBIT_ERROR });
        dispatch(formHandleInput("automaticDebit", "automaticDebitAmount", automaticDebitAmount));
        dispatch(formHandleInput("automaticDebit", "hasAutomaticDebit", hasAutomaticDebit));
      });
    }
    dispatch({ type: APP_LOADING_END });
  };

export const submitOrderFeedback =
  (
    orderId: string,
    formFeedback: any,
    source: "FrontCanteenWebAppPrompt" | "FrontCanteenWebAppOrdersHistory",
  ): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: APP_LOADING_START });
    try {
      const feedback: any = {};
      const feedbackTags: { name: string }[] = formFeedback.feedbackTags.map((tag: string) => {
        return { name: tag };
      });

      feedback.orderId = orderId;
      feedback.feedbackComment = formFeedback.feedbackComment;
      feedback.feedbackSource = source;
      feedback.feedbackIsSatisfied = formFeedback.isSatisfied;
      feedback.products = [];

      if (feedback.feedbackIsSatisfied === false) {
        feedback.feedbackTags = feedbackTags;
      }

      formFeedback.productFeedbacks.forEach((product: any) => {
        feedback.products.push({
          orderProductId: product.productId,
          feedbackRank: product.stars,
          feedbackComment: product.comment,
        });
      });
      const res = await apollo().mutate<any>({
        variables: { feedback },
        mutation: gql`
          mutation ($feedback: CanteenOrderFeedbackInput!) {
            submitCanteenOrderFeedback(feedback: $feedback)
          }
        `,
      });

      dispatch({ type: APP_LOADING_END });
      if (res.data.submitCanteenOrderFeedback) {
        dispatch({
          type: ACCOUNT_ORDERFEEDBACK_AVAILABLE,
          available: false,
          id: orderId,
        });
        return true;
      } else {
        return false;
      }
    } catch (err) {
      captureException(err);
      dispatch({ type: APP_LOADING_END });
      return false;
    }
  };

export const requestReceiptExport =
  (from: Date, to: Date): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: APP_LOADING_START });

    try {
      const {
        data: { requestReceiptExport },
      } = await apollo().mutate<any>({
        variables: { from, to },
        mutation: gql`
          mutation ($from: DateTime!, $to: DateTime!) {
            requestReceiptExport(from: $from, to: $to) {
              downloadUrl
              s3Key
              documentCount
            }
          }
        `,
      });

      if (!requestReceiptExport) {
        dispatch(
          addToast({
            type: "info",
            message:
              translateAccountMessage("account.toasts.receiptExportEmpty", {
                defaultValue: "Aucun reçu trouvé sur cette période.",
              }) ?? "Aucun reçu trouvé sur cette période.",
          }),
        );
        dispatch({ type: APP_LOADING_END });
        return;
      }

      const message =
        translateAccountMessage("account.toasts.receiptExportRequested", {
          defaultValue:
            "Votre export est prêt. Vous allez recevoir un mail avec le lien de téléchargement.",
        }) ?? "Export demandé";

      dispatch(
        addToast({
          type: "success",
          message,
        }),
      );
    } catch (err) {
      captureException(err);
      dispatch(
        addToast({
          type: "danger",
          message:
            translateAccountMessage("account.toasts.receiptExportError", {
              defaultValue: "Impossible de lancer l’export. Veuillez réessayer.",
            }) ?? "Erreur lors de l’export",
        }),
      );
    }

    dispatch({ type: APP_LOADING_END });
  };

export const discardFeedbackAvailable = (): AppThunk => async (dispatch: AppDispatch) => {
  await dispatch({
    type: ACCOUNT_ORDERFEEDBACK_AVAILABLE,
    available: false,
    id: null,
  });
};

export const getCredits = (): any => async (dispatch: AppDispatch) => {
  try {
    dispatch({ type: ACCOUNT_CREDITS_LOADING });
    dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    const req: any = await apollo().query({
      query: gql`
        query {
          credits {
            id
            amount
            executedAt
            isMealVoucher
          }
        }
      `,
    });

    dispatch({ type: ACCOUNT_CREDITS_SUCCESS, credits: req.data.credits });
  } catch (err) {
    captureException(err);

    dispatch({ type: ACCOUNT_CREDITS_ERROR });
  }
  dispatch({ type: APP_LOADING_END });
};

export const getOrderCountEligibleForSponsorship = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: APP_LOADING_START });
  try {
    const req: any = await apollo().query({
      query: gql`
        query {
          orderCountEligibleForSponsorship
        }
      `,
    });

    dispatch({
      type: ACCOUNT_ORDERSCOUNT_SET,
      ordersCount: req.data.orderCountEligibleForSponsorship,
    });
    dispatch({ type: APP_LOADING_END });
  } catch (err) {
    dispatch({ type: APP_LOADING_END });
    captureException(err);
  }
};

export const submitSponsorshipCode =
  (): AppThunk<Promise<boolean | undefined>> =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: ACCOUNT_SPONSORSHIPCODE_LOADING });
    dispatch({ type: APP_LOADING_START });

    try {
      const { form } = getState();
      const sponsorshipCode = form.sponsorship.code;
      const sponsorshipCodeErrorMessage = translateAccountMessage("account.sponsorship.codeError", {
        defaultValue:
          "Le code n'a pas pu être validé, veuillez vérifier votre saisie puis réessayez.",
      });

      const res = await apollo().mutate<any>({
        variables: { sponsorshipCode },
        mutation: gql`
          mutation ($sponsorshipCode: String!) {
            submitSponsorshipCode(sponsorshipCode: $sponsorshipCode)
          }
        `,
      });

      if (res.data.submitSponsorshipCode) {
        dispatch({ type: ACCOUNT_SPONSORSHIPCODE_SUCCESS });

        return true;
      } else {
        dispatch({
          type: ACCOUNT_SPONSORSHIPCODE_ERROR,
          errorMessage: sponsorshipCodeErrorMessage,
        });

        return false;
      }
    } catch (err) {
      captureException(err);

      dispatch({
        type: ACCOUNT_SPONSORSHIPCODE_ERROR,
        errorMessage: translateAccountMessage("account.sponsorship.codeError", {
          defaultValue:
            "Le code n'a pas pu être validé, veuillez vérifier votre saisie puis réessayez.",
        }),
      });
    }
    dispatch({ type: APP_LOADING_END });
  };

export const sendSponsorshipEmail =
  (): AppThunk<Promise<boolean | undefined>> =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: ACCOUNT_SPONSORSHIPEMAIL_LOADING });
    dispatch({ type: APP_LOADING_START });

    try {
      const { form } = getState();
      const sponsorshipEmail = form.sponsorshipEmail.email;
      const sponsorshipEmailErrorMessage = translateAccountMessage(
        "account.sponsorship.emailError",
        { defaultValue: "Votre collègue se régale déjà avec Popchef" },
      );

      const res = await apollo().mutate<any>({
        variables: { sponsorshipEmail },
        mutation: gql`
          mutation ($sponsorshipEmail: String!) {
            sendSponsorshipEmail(sponsorshipEmail: $sponsorshipEmail)
          }
        `,
      });

      if (res.data.sendSponsorshipEmail) {
        dispatch({ type: ACCOUNT_SPONSORSHIPEMAIL_SUCCESS });

        return true;
      } else {
        dispatch({
          type: ACCOUNT_SPONSORSHIPEMAIL_ERROR,
          errorMessage: sponsorshipEmailErrorMessage,
        });

        return false;
      }
    } catch (err) {
      captureException(err);

      dispatch({
        type: ACCOUNT_SPONSORSHIPEMAIL_ERROR,
        errorMessage: translateAccountMessage("account.sponsorship.emailError", {
          defaultValue: "Votre collègue se régale déjà avec Popchef",
        }),
      });
    }
    dispatch({ type: APP_LOADING_END });
  };

export const getLoyaltyProgramWallet = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: APP_LOADING_START });

  try {
    const req: any = await apollo().query({
      query: gql`
        query {
          loyaltyProgramWallet {
            balance
            id
          }
        }
      `,
    });

    const wallet = req.data.loyaltyProgramWallet;

    dispatch({
      type: LOYALTY_PROGRAM_WALLET_SET,
      loyaltyProgramWallet: wallet,
    });
  } catch (err) {
    captureException(err);
  }

  dispatch({ type: APP_LOADING_END });
};

export const getLoyaltyProgramMovements = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: APP_LOADING_START });

  try {
    const req: any = await apollo().query({
      query: gql`
        query LoyaltyProgramMovements {
          loyaltyProgramMovements {
            id
            movement
            type
            order {
              id
              totalWithVat
              createdAt
            }
          }
        }
      `,
    });

    const movements = req.data.loyaltyProgramMovements;

    dispatch({
      type: LOYALTY_PROGRAM_MOVEMENTS_SET,
      loyaltyProgramMovements: movements,
    });
  } catch (err) {
    captureException(err);
  }

  dispatch({ type: APP_LOADING_END });
};

export const redeemLoyaltyProgramLevel =
  (level: any): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: APP_LOADING_START });

    try {
      const result: any = await apollo().mutate({
        variables: { loyaltyProgramLevelId: level.id },
        mutation: gql`
          mutation RedeemLoyaltyProgramLevel($loyaltyProgramLevelId: String!) {
            redeemLoyaltyProgramLevel(loyaltyProgramLevelId: $loyaltyProgramLevelId)
          }
        `,
      });

      dispatch({ type: APP_LOADING_END });

      const redeemSuccessMessage = translateAccountMessage("account.toasts.redeemLevelSuccess", {
        params: { levelName: level.name },
        defaultValue:
          "Félicitations, vous avez débloqué le niveau " +
          level.name +
          " ! Votre cagnotte a été mise à jour.",
      });
      const redeemErrorMessage = translateAccountMessage("account.toasts.redeemLevelError", {
        params: { levelName: level.name },
        defaultValue:
          "Une erreur est survenue lors du déblocage du niveau " +
          level.name +
          " ! Veuillez réessayer.",
      });

      if (result.data.redeemLoyaltyProgramLevel) {
        dispatch(getLoyaltyProgramWallet());
        dispatch(getLoyaltyProgramMovements());
        dispatch(
          addToast({
            type: "success",
            message: redeemSuccessMessage,
          }),
        );
        return true;
      } else {
        dispatch(
          addToast({
            type: "error",
            message: redeemErrorMessage,
          }),
        );
        return false;
      }
    } catch (err) {
      dispatch({ type: APP_LOADING_END });
      captureException(err);
    }
  };

export const setEnrollToLoyaltyProgram =
  (enroll: boolean): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: APP_LOADING_START });

    try {
      const result: any = await apollo().mutate({
        variables: { shouldEnroll: enroll },
        mutation: gql`
          mutation SetEnrollToLoyaltyProgram($shouldEnroll: Boolean!) {
            setEnrollToLoyaltyProgram(shouldEnroll: $shouldEnroll)
          }
        `,
      });

      dispatch({ type: APP_LOADING_END });

      if (result.data.setEnrollToLoyaltyProgram) {
        await dispatch(getCustomerProfile());
        return true;
      } else {
        return false;
      }
    } catch (err) {
      captureException(err);
      dispatch({ type: APP_LOADING_END });
    }
  };
